import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
    CHURN_ZERO_APP_KEY,
    CHURN_ZERO_EVENT_TYPE,
    CHURN_ZERO_SDK_URL,
} from '../constants/churnZero';
import { IS_PROD } from '../constants/environment';
import { CLIENT_ENGAGEMENT } from '../constants/userEvents';
import { UserClientRole } from '../types/client';
import DomainContext from './domainContext';
import { FeatureFlagsContext } from './featureFlagsContext';
import UserContext from './userContext';

const {
    CRM_APP_KEY,
    CRM_EVENT_TRACK,
    CRM_SET_ATTRIBUTE,
    CRM_INCREMENT_ATTRIBUTE,
    CRM_SET_MODULE,
    CRM_URL_TRACKING,
    CRM_SILENT,
    CRM_OPEN,
    CRM_CLOSE,
    CRM_STOP,
    CRM_SET_CONTACT,
} = CHURN_ZERO_EVENT_TYPE;

export type EntityType = 'contact' | 'account';

interface Methods {
    setContact: (accountId: string, contactId: string) => void;
    trackEvent: <T>(
        eventName: string,
        description?: string,
        quantity?: number,
        customFields?: Record<string, T>,
    ) => void;
    setAttribute: <T>(entity: EntityType, name: string, value: T) => void;
    incrementAttribute: (entity: EntityType, name: string, value: number) => void;
    setModule: (moduleName: string) => void;
    urltracking: (isUrlTracking: boolean) => void;
    silent: (isSilent: boolean) => void;
    open: () => void;
    close: () => void;
    stop: () => void;
}

interface ChurnZero {
    push: (...args: unknown[]) => void;
}

interface ChurnZeroClient {
    methods?: Methods;
    isReady?: boolean;
    setAccountAndContactAttributes?: (params: AccountAndContactParams) => void;
}

interface AccountAndContactParams {
    firstName: string;
    lastName: string;
    email: string;
}

const ChurnZeroContext = createContext<ChurnZeroClient>({
    isReady: false,
});

let hasLoggedIn = false;

const ChurnZeroProvider = ({ children }: { children: React.ReactNode }) => {
    const [methods, setMethods] = useState<ChurnZero>();
    const [isReady, setIsReady] = useState(false);

    const { activeClient, fetchedUser, firstName, lastName, email } = useContext(UserContext);
    const { CHURN_ZERO } = useContext(FeatureFlagsContext);

    const domainContextVal = useContext(DomainContext);

    const domainClient = domainContextVal?.domainData?.client;

    const { salesforceId } = activeClient || domainClient || {};

    useEffect(() => {
        async function initialize() {
            if (CHURN_ZERO) {
                await embedScript(IS_PROD ? CHURN_ZERO_SDK_URL.PROD : CHURN_ZERO_SDK_URL.DEV);
                const churnZero = window.ChurnZero;
                if (!churnZero) {
                    throw new Error('ChurnZero object is not initialized by embedded script.');
                }

                churnZero.push([
                    CRM_APP_KEY,
                    IS_PROD ? CHURN_ZERO_APP_KEY.PROD : CHURN_ZERO_APP_KEY.DEV,
                ]);
                setMethods(churnZero);
                setIsReady(!!salesforceId);
            }
        }

        initialize();
    }, [salesforceId, CHURN_ZERO]);

    const getLoginType = React.useCallback((role: UserClientRole) => {
        if (role === 'internal_client' || role === 'admin') return CLIENT_ENGAGEMENT.CUSTOMER_LOGIN;
        if (role === 'user') return CLIENT_ENGAGEMENT.USER_LOGIN;
        if (role === 'internal_cs') return CLIENT_ENGAGEMENT.CS_LOGIN;
        return undefined;
    }, []);

    const trackEvent = useCallback(
        (...args: Parameters<Methods['trackEvent']>) => {
            methods?.push([CRM_EVENT_TRACK, ...args]);
        },
        [methods],
    );

    const setAccountAndContactAttributes = useCallback(
        (accAndContactParams?: AccountAndContactParams) => {
            // We are current defaulting to use the app state values over manually input values.
            // This is because I do not fully trust this process yet and want to only make those
            // values manually writiable if they don't exist in the app state.
            if (
                isReady &&
                CHURN_ZERO &&
                (fetchedUser || accAndContactParams) &&
                (activeClient || domainClient) &&
                methods
            ) {
                const { id: accountId, accountName } = activeClient || domainClient;
                methods?.push([CRM_SET_CONTACT, salesforceId, email || accAndContactParams?.email]);
                methods?.push([CRM_SET_ATTRIBUTE, 'account', 'Name', accountName]);
                methods?.push([CRM_SET_ATTRIBUTE, 'account', 'clientAccountId', accountId]);
                methods?.push([CRM_SET_ATTRIBUTE, 'account', 'clientSalesforceId', salesforceId]);
                methods?.push([
                    CRM_SET_ATTRIBUTE,
                    'contact',
                    'ContactRole',
                    activeClient?.userClients?.role || 'user',
                ]);
                methods?.push([
                    CRM_SET_ATTRIBUTE,
                    'contact',
                    'firstName',
                    firstName || accAndContactParams?.firstName,
                ]);
                methods?.push([
                    CRM_SET_ATTRIBUTE,
                    'contact',
                    'lastName',
                    lastName || accAndContactParams?.lastName,
                ]);
            }
        },
        [
            isReady,
            firstName,
            lastName,
            email,
            fetchedUser,
            activeClient,
            methods,
            domainClient,
            salesforceId,
            CHURN_ZERO,
        ],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (isReady && fetchedUser && activeClient && methods) {
            setAccountAndContactAttributes();
            const loginRole = getLoginType(activeClient.userClients.role);
            if (loginRole && !hasLoggedIn) {
                trackEvent(loginRole);
                hasLoggedIn = true;
            }
        }
    }, [
        isReady,
        fetchedUser,
        activeClient,
        methods,
        getLoginType,
        trackEvent,
        salesforceId,
        email,
        firstName,
        lastName,
        setAccountAndContactAttributes,
    ]);

    async function embedScript(url: string) {
        return new Promise<void>((resolve, reject) => {
            const f = document.getElementsByTagName('script')[0];
            const j = document.createElement('script');
            j.async = true;
            j.src = url;
            j.crossOrigin = 'anonymous';
            f.parentNode?.insertBefore(j, f);

            j.onload = () => {
                resolve();
            };

            j.onerror = (event, __, ___, ____, error) => {
                reject(
                    new Error(
                        `Failed to load ChurnZero script. ${
                            typeof event === 'string' ? event : ''
                        } ${error?.message}`,
                    ),
                );
            };
        });
    }

    const setAttribute = useCallback(
        function setAttr<T>(entity: EntityType, name: string, value: T) {
            methods?.push([CRM_SET_ATTRIBUTE, entity, name, value]);
        },
        [methods],
    );

    const setContact = useCallback(
        (accountId: string, contactId: string) => {
            methods?.push([CRM_SET_CONTACT, accountId, contactId]);
        },
        [methods],
    );

    const incrementAttribute = useCallback(
        (entity: EntityType, name: string, value: number) => {
            methods?.push([CRM_INCREMENT_ATTRIBUTE, entity, name, value]);
        },
        [methods],
    );

    const setModule = useCallback(
        (...args: Parameters<Methods['setModule']>) => {
            methods?.push([CRM_SET_MODULE, ...args]);
        },
        [methods],
    );

    const urltracking = useCallback(
        (...args: Parameters<Methods['urltracking']>) => {
            methods?.push([CRM_URL_TRACKING, ...args]);
        },
        [methods],
    );

    const silent = useCallback(
        (...args: Parameters<Methods['silent']>) => {
            methods?.push([CRM_SILENT, ...args]);
        },
        [methods],
    );

    const open = useCallback(
        (...args: Parameters<Methods['open']>) => {
            methods?.push([CRM_OPEN, ...args]);
        },
        [methods],
    );

    const close = useCallback(
        (...args: Parameters<Methods['close']>) => {
            methods?.push([CRM_CLOSE, ...args]);
        },
        [methods],
    );

    const stop = useCallback(
        (...args: Parameters<Methods['stop']>) => {
            methods?.push([CRM_STOP, ...args]);
        },
        [methods],
    );

    return methods ? (
        <ChurnZeroContext.Provider
            value={{
                methods: {
                    setContact,
                    trackEvent,
                    setAttribute,
                    incrementAttribute,
                    setModule,
                    urltracking,
                    silent,
                    open,
                    close,
                    stop,
                },
                isReady,
                setAccountAndContactAttributes,
            }}
        >
            {children}
        </ChurnZeroContext.Provider>
    ) : (
        <>{children}</>
    );
};

const useChurnZero = () => {
    const { methods, setAccountAndContactAttributes, isReady } = useContext(ChurnZeroContext);
    const { CHURN_ZERO } = useContext(FeatureFlagsContext);

    if (!methods && CHURN_ZERO) {
        throw new Error('useChurnZero must be used within a ChurnZeroProvider component');
    }

    return { methods, setAccountAndContactAttributes, isReady };
};

export { ChurnZeroProvider, useChurnZero };
